import type { SupabaseClient } from "@supabase/supabase-js";

import type { ExecutionJobRepository } from "./jobRepository";

import type {
  ExecutionCheckpoint,
  ExecutionJob,
  ExecutionJobEvent,
  ExecutionJobEventType,
  ExecutionJobStatus,
} from "./jobTypes";

type DbJob = {
  id: string;
  organization_id: string;
  product_id: string | null;
  project_id: string | null;
  work_item_id: string | null;
  phase_id: string;

  title: string;
  objective: string;

  source_spec_path: string;
  source_spec_version: string | null;
  source_spec_approved: boolean;

  department: string;
  risk: ExecutionJob["risk"];
  status: ExecutionJobStatus;

  assigned_agent_id: string | null;

  attempt: number;

  budget: ExecutionJob["budget"];

  lease_owner: string | null;
  lease_expires_at: string | null;

  last_checkpoint_id: string | null;

  created_at: string;
  updated_at: string;
};

type DbCheckpoint = {
  id: string;
  execution_job_id: string;
  status: ExecutionJobStatus;
  summary: string;
  evidence_references: string[];
  attempt: number;
  created_at: string;
};

type DbEvent = {
  id: string;
  execution_job_id: string;
  event_type: ExecutionJobEventType;
  summary: string;
  metadata: Record<string, unknown>;
  created_at: string;
};

function mapJob(row: DbJob): ExecutionJob {
  if (!row.work_item_id) {
    throw new Error(`Execution job ${row.id} has no work_item_id.`);
  }

  return {
    id: row.id,

    organizationId: row.organization_id,

    productId: row.product_id ?? undefined,

    projectId: row.project_id ?? undefined,

    workItemId: row.work_item_id,

    phaseId: row.phase_id,

    title: row.title,

    objective: row.objective,

    sourceSpec: {
      path: row.source_spec_path,

      version: row.source_spec_version ?? undefined,

      approved: row.source_spec_approved,
    },

    department: row.department as ExecutionJob["department"],

    risk: row.risk,

    budget: row.budget,

    status: row.status,

    assignedAgentId: row.assigned_agent_id ?? undefined,

    attempt: row.attempt,

    leaseOwner: row.lease_owner ?? undefined,

    leaseExpiresAt: row.lease_expires_at ?? undefined,

    lastCheckpointId: row.last_checkpoint_id ?? undefined,

    createdAt: row.created_at,

    updatedAt: row.updated_at,
  };
}

function serializeJob(job: ExecutionJob) {
  return {
    id: job.id,

    organization_id: job.organizationId,

    product_id: job.productId ?? null,

    project_id: job.projectId ?? null,

    work_item_id: job.workItemId,

    phase_id: job.phaseId,

    title: job.title,

    objective: job.objective,

    source_spec_path: job.sourceSpec.path,

    source_spec_version: job.sourceSpec.version ?? null,

    source_spec_approved: job.sourceSpec.approved,

    department: job.department,

    risk: job.risk,

    status: job.status,

    assigned_agent_id: job.assignedAgentId ?? null,

    attempt: job.attempt,

    budget: job.budget,

    lease_owner: job.leaseOwner ?? null,

    lease_expires_at: job.leaseExpiresAt ?? null,

    last_checkpoint_id: job.lastCheckpointId ?? null,

    created_at: job.createdAt,

    updated_at: job.updatedAt,
  };
}

function mapCheckpoint(row: DbCheckpoint): ExecutionCheckpoint {
  return {
    id: row.id,

    executionJobId: row.execution_job_id,

    status: row.status,

    summary: row.summary,

    evidenceReferences: row.evidence_references ?? [],

    attempt: row.attempt,

    createdAt: row.created_at,
  };
}

function mapEvent(row: DbEvent): ExecutionJobEvent {
  return {
    id: row.id,

    executionJobId: row.execution_job_id,

    type: row.event_type,

    summary: row.summary,

    metadata: row.metadata ?? {},

    createdAt: row.created_at,
  };
}

export class SupabaseExecutionJobRepository implements ExecutionJobRepository {
  constructor(private readonly client: SupabaseClient) {}

  async createJob(job: ExecutionJob): Promise<ExecutionJob> {
    const { data, error } = await this.client
      .from("execution_jobs")
      .insert(serializeJob(job))
      .select("*")
      .single();

    if (error) {
      throw new Error(`Failed to create execution job: ${error.message}`);
    }

    return mapJob(data as DbJob);
  }

  async getJob(jobId: string): Promise<ExecutionJob | undefined> {
    const { data, error } = await this.client
      .from("execution_jobs")
      .select("*")
      .eq("id", jobId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to read execution job: ${error.message}`);
    }

    return data ? mapJob(data as DbJob) : undefined;
  }

  async listJobs(statuses?: ExecutionJobStatus[]): Promise<ExecutionJob[]> {
    let query = this.client
      .from("execution_jobs")
      .select("*")
      .order("created_at", {
        ascending: true,
      });

    if (statuses && statuses.length > 0) {
      query = query.in("status", statuses);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to list execution jobs: ${error.message}`);
    }

    return ((data ?? []) as DbJob[]).map(mapJob);
  }

  async saveJob(job: ExecutionJob): Promise<void> {
    const { error } = await this.client
      .from("execution_jobs")
      .update(serializeJob(job))
      .eq("id", job.id);

    if (error) {
      throw new Error(`Failed to save execution job: ${error.message}`);
    }
  }

  async claimNextJob(input: {
    workerId: string;
    leaseDurationMs: number;
    now?: Date;
  }): Promise<ExecutionJob | undefined> {
    const now = input.now ?? new Date();

    const expires = new Date(
      now.getTime() + input.leaseDurationMs,
    ).toISOString();

    /*
     * Recover leases left behind by a crashed/restarted persistent worker.
     *
     * A job whose lease expired is durable work and must become claimable
     * again instead of remaining stranded forever.
     */
    const { error: staleLeaseError } = await this.client
      .from("execution_jobs")
      .update({
        status: "QUEUED",
        lease_owner: null,
        lease_expires_at: null,
        updated_at: now.toISOString(),
      })
      .in("status", ["CLAIMED", "EXECUTING", "VERIFYING"])
      .lt("lease_expires_at", now.toISOString());

    if (staleLeaseError) {
      throw new Error(
        `Failed to recover expired execution leases: ${staleLeaseError.message}`,
      );
    }

    const { data: candidate, error } = await this.client
      .from("execution_jobs")
      .select("*")
      .eq("status", "QUEUED")
      /*
       * Queue fairness:
       *
       * updated_at changes whenever a job is requeued. Ordering by it means
       * a repeatedly failing job yields to work that has been waiting longer
       * since its last execution opportunity.
       *
       * This prevents one resumable poison/recovery loop from starving the
       * rest of the durable queue indefinitely.
       */
      .order("updated_at", {
        ascending: true,
      })
      .order("created_at", {
        ascending: true,
      })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to inspect execution queue: ${error.message}`);
    }

    if (!candidate) {
      return undefined;
    }

    console.log(
      `[cyan-queue] claim candidate=${candidate.id} worker=${input.workerId}`,
    );

    const { data, error: claimError } = await this.client
      .from("execution_jobs")
      .update({
        status: "CLAIMED",

        lease_owner: input.workerId,

        lease_expires_at: expires,

        updated_at: now.toISOString(),
      })
      .eq("id", candidate.id)
      .eq("status", "QUEUED")
      .select("*")
      .maybeSingle();

    if (claimError) {
      console.error(
        `[cyan-queue] claim update failed candidate=${candidate.id}: ${claimError.message}`,
      );

      throw new Error(
        `Failed to claim execution job ${candidate.id}: ${claimError.message}`,
      );
    }

    if (!data) {
      /*
       * A visible QUEUED candidate followed by an empty UPDATE result is not
       * equivalent to an empty queue.
       *
       * It means the claim lost a race or the write path could not affect the
       * row. Re-read the candidate so the runtime gets authoritative evidence
       * instead of silently returning undefined.
       */
      const { data: current, error: currentError } = await this.client
        .from("execution_jobs")
        .select("*")
        .eq("id", candidate.id)
        .maybeSingle();

      if (currentError) {
        throw new Error(
          `Claim returned no row and candidate re-read failed ${candidate.id}: ${currentError.message}`,
        );
      }

      if (!current) {
        throw new Error(
          `Claim candidate disappeared during claim: ${candidate.id}`,
        );
      }

      console.error(
        `[cyan-queue] claim affected zero rows candidate=${candidate.id} currentStatus=${current.status} leaseOwner=${current.lease_owner ?? "none"}`,
      );

      if (
        current.status === "CLAIMED" &&
        current.lease_owner === input.workerId
      ) {
        return mapJob(current as DbJob);
      }

      return undefined;
    }

    console.log(
      `[cyan-queue] claim success candidate=${candidate.id} worker=${input.workerId}`,
    );

    return mapJob(data as DbJob);
  }

  async renewLease(
    jobId: string,
    workerId: string,
    leaseDurationMs: number,
    now: Date = new Date(),
  ): Promise<void> {
    const expires = new Date(now.getTime() + leaseDurationMs).toISOString();

    const { error } = await this.client
      .from("execution_jobs")
      .update({
        lease_expires_at: expires,
        updated_at: now.toISOString(),
      })
      .eq("id", jobId)
      .eq("lease_owner", workerId);

    if (error) {
      throw new Error(`Failed to renew execution lease: ${error.message}`);
    }
  }

  async releaseLease(jobId: string, workerId: string): Promise<void> {
    const now = new Date().toISOString();

    const { error } = await this.client
      .from("execution_jobs")
      .update({
        lease_owner: null,

        lease_expires_at: null,

        updated_at: now,
      })
      .eq("id", jobId)
      .eq("lease_owner", workerId);

    if (error) {
      throw new Error(`Failed to release execution lease: ${error.message}`);
    }
  }

  async saveCheckpoint(checkpoint: ExecutionCheckpoint): Promise<void> {
    const { error } = await this.client.from("execution_checkpoints").insert({
      id: checkpoint.id,

      execution_job_id: checkpoint.executionJobId,

      status: checkpoint.status,

      summary: checkpoint.summary,

      evidence_references: checkpoint.evidenceReferences,

      attempt: checkpoint.attempt,

      created_at: checkpoint.createdAt,
    });

    if (error) {
      throw new Error(`Failed to save execution checkpoint: ${error.message}`);
    }
  }

  async listCheckpoints(jobId: string): Promise<ExecutionCheckpoint[]> {
    const { data, error } = await this.client
      .from("execution_checkpoints")
      .select("*")
      .eq("execution_job_id", jobId)
      .order("created_at", {
        ascending: true,
      });

    if (error) {
      throw new Error(`Failed to list execution checkpoints: ${error.message}`);
    }

    return ((data ?? []) as DbCheckpoint[]).map(mapCheckpoint);
  }

  async appendEvent(event: ExecutionJobEvent): Promise<void> {
    const { error } = await this.client.from("execution_job_events").insert({
      id: event.id,

      execution_job_id: event.executionJobId,

      event_type: event.type,

      summary: event.summary,

      metadata: event.metadata,

      created_at: event.createdAt,
    });

    if (error) {
      throw new Error(`Failed to append execution event: ${error.message}`);
    }
  }

  async listEvents(jobId: string): Promise<ExecutionJobEvent[]> {
    const { data, error } = await this.client
      .from("execution_job_events")
      .select("*")
      .eq("execution_job_id", jobId)
      .order("created_at", {
        ascending: true,
      });

    if (error) {
      throw new Error(`Failed to list execution events: ${error.message}`);
    }

    return ((data ?? []) as DbEvent[]).map(mapEvent);
  }
  async recoverFailedJobsForAutonomousContinuation(): Promise<number> {
    const { data, error } = await this.client
      .from("execution_jobs")
      .select("id,attempt,status")
      .eq("status", "FAILED");

    if (error) {
      throw new Error(
        `Failed to inspect recoverable execution jobs: ${error.message}`,
      );
    }

    let recovered = 0;

    for (const job of data ?? []) {
      const nextAttempt =
        typeof job.attempt === "number" ? job.attempt + 1 : 1;

      const { error: updateError } = await this.client
        .from("execution_jobs")
        .update({
          status: "QUEUED",
          attempt: nextAttempt,
          lease_owner: null,
          lease_expires_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", job.id)
        .eq("status", "FAILED");

      if (updateError) {
        throw new Error(
          `Failed to recover execution job ${job.id}: ${updateError.message}`,
        );
      }

      recovered += 1;
    }

    return recovered;
  }

  async recoverOrphanExecutingJobs(): Promise<number> {
    const now = new Date().toISOString();

    const { data, error } = await this.client
      .from("execution_jobs")
      .select("id,status,attempt,lease_owner,lease_expires_at")
      .eq("status", "EXECUTING")
      .is("lease_owner", null);

    if (error) {
      /*
       * Infrastructure/network failure is transient.
       * Never convert it to owner-action or destructive failure.
       */
      throw new Error(
        `Transient execution recovery inspection failed: ${error.message}`,
      );
    }

    let recovered = 0;

    for (const job of data ?? []) {
      const { error: updateError } = await this.client
        .from("execution_jobs")
        .update({
          status: "QUEUED",
          lease_owner: null,
          lease_expires_at: null,
          updated_at: now,
        })
        .eq("id", job.id)
        .eq("status", "EXECUTING")
        .is("lease_owner", null);

      if (updateError) {
        throw new Error(
          `Transient orphan execution recovery failed for ${job.id}: ${updateError.message}`,
        );
      }

      recovered += 1;
    }

    return recovered;
  }

}
